import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'
import { useContext } from 'react'

import { productPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityProductHeroModule } from '@data/sanity/queries/types/modules'
import { type SanityProductPage } from '@data/sanity/queries/types/page'
import { type SanityCombinedListingProduct } from '@data/sanity/queries/types/product'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { useViewProductEvent } from '@lib/analytics-context'
import { getNonNullValues } from '@lib/helpers'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { ProductContext } from '@lib/product/context'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
} from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllProductSlugs,
} from '@lib/sanity/page'
import { getProductPage } from '@lib/sanity/product'
import { getProductSchema } from '@lib/schema/product'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { ShopContext } from '@lib/shop-context'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ProductPageProps = BasePageProps & {
  slug: string
  page: SanityProductPage
  site: SanitySiteFragment
  product: SanityCombinedListingProduct
}

type StaticProductPageParams = ParsedUrlQuery & {
  slug: string
}

/**
 * The product page.
 */
const ProductPage = ({
  draftMode,
  locale,
  slug,
  page,
  site,
}: ProductPageProps) => {
  const { currencyCode, taxRate } = useContext(ShopContext)
  const { product, activeVariant } = useContext(ProductContext)

  // Get product schema
  const productSchema = getProductSchema(
    locale,
    product,
    activeVariant,
    site.seoSettings?.siteTitle,
    site.generalSettings.siteURL,
    currencyCode,
    taxRate,
  )
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [productSchema, ...videoObjectSchemas]

  useViewProductEvent()
  // Deprecated. Encourage clients to use GTM instead.
  // useViewContentFacebookEvent(
  //   locale,
  //   product?.title,
  //   activeVariant?.variantID,
  //   activeVariant?.options,
  //   site.generalSettings?.facebookEvents
  // )

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={productPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            productSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticProductPageParams>,
): Promise<GetStaticPropsResult<ProductPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const productPage = await getProductPage(sanityClient, context.locale, slug)

  // Get product from product hero module
  const pageModules = productPage.page?.modules ?? []
  const productHeroModule = pageModules.find(
    (pageModule) => pageModule._type === 'productHero',
  ) as SanityProductHeroModule | undefined
  const product = productHeroModule?.product

  if (!productPage.page || !product) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      page: getNonNullValues(productPage.page),
      site: getNonNullValues(productPage.site),
      product,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticProductPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allProducts = await getAllProductSlugs(sanityClient)
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allProducts
    .map((product) => ({
      ...product,
      slug: product.slug.current,
      locale: product.locale as Locale,
    }))
    .filter((product) => allLocales.includes(product.locale))
    .filter((product) => !!product.slug)
    .map((product) => ({
      params: {
        slug: product.slug,
      },
      locale: product.locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ProductPage
